import React, { useState } from "react";
import axios from "axios";
import { Download, BookOpen, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/context/LanguageContext";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = "/lead-magnet/50-expressoes-francesas.pdf";

const COPY = {
  pt: {
    overline: "Guia gratuito · Nível A2 → B2",
    title: "50 expressões francesas que todo brasileiro deveria conhecer.",
    subtitle:
      "12 páginas com as expressões idiomáticas que separam o francês de escola do francês de verdade — falado em qualquer padaria, metrô ou escritório na França. Cada expressão com tradução literal (pra rir), significado real e exemplo de uso. Pronto pra ler no celular.",
    bullets: [
      "50 expressões testadas com brasileiros reais no Rio",
      "Tradução literal + significado + frase de exemplo",
      "Selecionado pelo Prof Lionel, parisiense de nascimento",
      "PDF de 12 páginas, 100% grátis, sem pegadinha",
    ],
    nameLabel: "Seu primeiro nome",
    emailLabel: "Seu melhor e-mail",
    consent:
      "Aceito receber o PDF e, ocasionalmente, dicas de francês do Prof Lionel. Sem spam — descadastre quando quiser.",
    cta: "Receber meu guia em PDF",
    submitting: "Enviando…",
    successTitle: "Voilà ! Seu guia já está a caminho.",
    successBody: "Verifique sua caixa de entrada (e a pasta de spam) nos próximos minutos.",
    successCta: "Baixar agora mesmo",
    errorGeneric: "Ops, algo deu errado. Tente novamente em instantes.",
    errorRate: "Você fez muitas tentativas. Tente novamente em uma hora.",
  },
  en: {
    overline: "Free guide · Level A2 → B2",
    title: "50 French expressions every Brazilian should know.",
    subtitle:
      "12 pages of the idiomatic expressions that separate textbook French from real French — spoken in any bakery, metro or office in France. Each one with literal translation (for the laughs), real meaning and example sentence. Mobile-friendly PDF.",
    bullets: [
      "50 expressions road-tested with real Brazilians in Rio",
      "Literal translation + real meaning + example sentence",
      "Curated by Prof Lionel, native Parisian",
      "12-page PDF, 100% free, no strings attached",
    ],
    nameLabel: "Your first name",
    emailLabel: "Your best email",
    consent:
      "I agree to receive the PDF and, occasionally, French tips from Prof Lionel. No spam — unsubscribe anytime.",
    cta: "Send me the PDF guide",
    submitting: "Sending…",
    successTitle: "Voilà! Your guide is on its way.",
    successBody: "Check your inbox (and spam folder) in the next few minutes.",
    successCta: "Download now",
    errorGeneric: "Something went wrong. Please try again shortly.",
    errorRate: "Too many attempts. Please try again in an hour.",
  },
  fr: {
    overline: "Guide offert · Niveau A2 → B2",
    title: "50 expressions françaises que tout Brésilien devrait connaître.",
    subtitle:
      "12 pages des expressions idiomatiques qui séparent le français scolaire du vrai français — celui parlé dans n'importe quelle boulangerie, métro ou bureau en France. Chacune avec sa traduction littérale (pour rire), son vrai sens et un exemple. PDF optimisé mobile.",
    bullets: [
      "50 expressions testées avec de vrais Brésiliens à Rio",
      "Traduction littérale + sens réel + phrase d'exemple",
      "Sélectionnées par le Prof Lionel, Parisien d'origine",
      "PDF de 12 pages, 100 % offert, sans engagement",
    ],
    nameLabel: "Votre prénom",
    emailLabel: "Votre meilleur e-mail",
    consent:
      "J'accepte de recevoir le PDF et, occasionnellement, des conseils de français du Prof Lionel. Aucun spam — désabonnement à tout moment.",
    cta: "Envoyez-moi le guide",
    submitting: "Envoi…",
    successTitle: "Voilà ! Votre guide arrive.",
    successBody: "Vérifiez votre boîte de réception (et les spams) dans quelques minutes.",
    successCta: "Télécharger maintenant",
    errorGeneric: "Une erreur est survenue. Réessayez dans un instant.",
    errorRate: "Trop de tentatives. Réessayez dans une heure.",
  },
};

export const useLeadMagnet = (options = {}) => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  const resource = options.resource || "50_expressoes";
  const downloadUrl = options.downloadUrl || PDF_URL;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e, ctx = "section") => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/lead-magnet`, {
        name: name.trim(),
        email: email.trim(),
        consent,
        language: lang,
        resource,
        website,
      });
      track("lead_magnet_submitted", { ctx, lang, resource });
      setSuccess(true);
      // Open the download in a new tab as immediate gratification
      try {
        window.open(downloadUrl, "_blank", "noopener");
      } catch (_e) { void _e;
        /* ignore popup blocked */
      }
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 429 ? t.errorRate : t.errorGeneric);
      track("lead_magnet_error", { ctx, status, resource });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    t,
    state: {
      name,
      setName,
      email,
      setEmail,
      consent,
      setConsent,
      website,
      setWebsite,
      submitting,
      success,
      error,
    },
    onSubmit,
    pdfUrl: downloadUrl,
  };
};

export default function LeadMagnet() {
  const { t, state, onSubmit, pdfUrl } = useLeadMagnet();
  return (
    <section
      id="guia-50-expressoes"
      data-testid="lead-magnet-section"
      className="bg-[#1e2a5a] text-[#f4f1ea] py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <p className="overline text-[#ff8a7a]">{t.overline}</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            {t.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#d4d2cb] leading-relaxed max-w-xl">
            {t.subtitle}
          </p>
          <ul className="mt-10 space-y-3">
            {t.bullets.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm sm:text-base text-[#e6e4dd]"
              >
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-[#ff8a7a]" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-[#f4f1ea] text-[#1a1a1a] rounded-2xl p-8 sm:p-10 shadow-2xl border border-[#1e2a5a]/20">
          {state.success ? (
            <div data-testid="lead-magnet-success" className="text-center py-6">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#1e7a3e]/10 text-[#1e7a3e] mb-5">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl text-[#1e2a5a] tracking-tight">
                {t.successTitle}
              </h3>
              <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">
                {t.successBody}
              </p>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-magnet-download-link"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e]"
              >
                <Download className="h-4 w-4" /> {t.successCta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          ) : (
            <form onSubmit={(e) => onSubmit(e, "section")} className="space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#1e2a5a]/5 px-3 py-1.5 text-xs text-[#1e2a5a] mb-2">
                <BookOpen className="h-3.5 w-3.5" /> PDF · 12 páginas · 100% grátis
              </div>
              <label className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
                  {t.nameLabel}
                </span>
                <Input
                  data-testid="lead-magnet-name"
                  type="text"
                  required
                  autoComplete="given-name"
                  value={state.name}
                  onChange={(e) => state.setName(e.target.value)}
                  className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
                  {t.emailLabel}
                </span>
                <Input
                  data-testid="lead-magnet-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={state.email}
                  onChange={(e) => state.setEmail(e.target.value)}
                  className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
                />
              </label>

              {/* Honeypot — hidden from real users */}
              <input
                type="text"
                tabIndex="-1"
                autoComplete="off"
                value={state.website}
                onChange={(e) => state.setWebsite(e.target.value)}
                className="hidden"
                aria-hidden="true"
              />

              <label className="flex items-start gap-3 text-xs text-[#5c5c5c] leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="lead-magnet-consent"
                  checked={state.consent}
                  onChange={(e) => state.setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#1e2a5a]/30 accent-[#1e2a5a]"
                />
                <span>{t.consent}</span>
              </label>

              {state.error && (
                <p
                  data-testid="lead-magnet-error"
                  className="text-sm text-[#d9381e]"
                >
                  {state.error}
                </p>
              )}

              <button
                type="submit"
                data-testid="lead-magnet-submit"
                disabled={state.submitting || !state.consent}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#d9381e] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#1e2a5a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> {t.submitting}
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" /> {t.cta}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
